import {
  tooManyDigitsAfterDecimal,
  maxYearOfCompletion,
  minYearOfCompletion,
  maxScore,
  minScore,
  minCTC,
} from "./library";

// these utility methods don't check input error. That is done by "pattern" html attribute on form elements. But, if there is an input error, we use these to fetch the error message

// used for validation of name, graduation college name, post graduation college name and current company name
export function getCommonErrorMessage(value, label, required) {
  const len = value.length;
  value = value.trim();
  var pattern = "[a-z|A-Z]{1}[a-z|A-Z|.\\s]{1}(a-z|A-Z| |.)*";
  if (required && !value) {
    return `${label} can't be blank`;
  } else if (!value && len > 0) {
    return `${label} is invalid.`;
  } else if (!value.match(pattern)) {
    return `${label} is invalid.`;
  } else if (required && value.length < 2) {
    return `${label} must have atleast 2 characters`;
  } else {
    return "";
  }
}

export function getCompanyErrorMessage(value, label, required) {
  const len = value.length;
  value = value.trim();
  var pattern = "[a-z|A-Z]{1}[a-z|A-Z|.&\\s]{1}(a-z|A-Z| |.)*";
  if (required && !value && len > 0) {
    return `${label} is invalid`;
  } else if (required && !value) {
    return `${label} can't be blank`;
  } else if (required && value.length < 2) {
    return `${label} must have atleast 2 characters`;
  } else if (!value.match(pattern)) {
    return `${label} is invalid.`;
  } else {
    return "";
  }
}

export function getNameErrorMessage(value, label, required) {
  const val = value;
  value = value.trim();
  var letters = /^[A-Za-z]+$/;
  var nameRegex = /^[A-Za-z]([-']?[A-Za-z]+)*( [A-Za-z]([-']?[A-Za-z]+)*){0,}$/;
  if (required && !value) {
    return `${label} can't be blank`;
  } else if (value.length < 2) {
    return `${label} must have atleast 2 characters`;
  } else if (!nameRegex.test(val)) {
    return `${label} value is invalid.`;
  } else if (!value.charAt(0).match(letters)) {
    return `${label} must start with a letter`;
  } else {
    return "";
  }
}

export function getEmailErrorMessage(inputRef, email) {
  if (!inputRef.current) {
    return "";
  }

  // eslint-disable-next-line no-useless-escape
  const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  // clearing errors if any
  if (emailRegex.test(email)) {
    inputRef.current.setCustomValidity("");
  } else {
    if (!email) {
      inputRef.current.setCustomValidity("Email can't be blank");
      return "Email can't be blank";
    } else {
      inputRef.current.setCustomValidity("Email is invalid");
      return "Email is invalid";
    }
  }
}

export function getPhoneNumberErrorMessage(phoneNumber) {
  // phone number is required
  if (!phoneNumber) {
    return "Phone Number can't be blank";
  }

  // error if number has non-digits or has decimal
  if (
    isNaN(Number(phoneNumber)) ||
    !Number.isInteger(Number(phoneNumber)) ||
    phoneNumber.includes(" ")
  ) {
    return "Phone Number is invalid";
  }

  // error if phone number is not 10 digits
  else if (phoneNumber.length !== 10) {
    return "Phone number must be 10 digits long";
  }
}

export function getAlternatePhoneNumberErrorMessage(
  inputRef,
  phoneNumber,
  alternatePhoneNumber
) {
  if (
    (alternatePhoneNumber.length > 0 &&
      alternatePhoneNumber.trim().length === 0) ||
    !alternatePhoneNumber.match("[0-9]{10}")
  ) {
    return "Alternate Phone Number is invalid";
  }

  if (inputRef.current) {
    // alternate phone number is not empty and the 2 numbers match
    if (alternatePhoneNumber && phoneNumber === alternatePhoneNumber) {
      inputRef.current.setCustomValidity("Phone numbers are identical");
      return "Phone numbers are identical";
    } else {
      inputRef.current.setCustomValidity("");
    }
  }

  if (!alternatePhoneNumber) {
    return "";
  }

  // error if number has non-digits or has decimal
  else if (
    isNaN(Number(phoneNumber)) ||
    !Number.isInteger(Number(phoneNumber)) ||
    phoneNumber.includes(" ")
  ) {
    return "Phone Number is invalid";
  }
  // error if phone number is not between 10 to 12 digits
  else if (
    alternatePhoneNumber.length < 10 ||
    alternatePhoneNumber.length > 12
  ) {
    return "Phone number must be 10-12 digits long";
  }
}

// for a bad input, the value of an input field is set to '' and is indistinguishable from '' or blank input. So, we are checking validity.badInput for verification
export function getScoreErrorMessage(inputRef, score, required) {
  var isInputBad;
  const scoreNumber = Number(score);

  if (!inputRef.current) {
    if (required) {
      return "Score can't be blank";
    } else {
      return "";
    }
  } else {
    isInputBad = inputRef.current.validity.badInput;
    // required fields can't be blank
    if (required && !isInputBad && !score) {
      return "Score can't be blank";
    }
  }

  // remaining validations
  if (isNaN(scoreNumber) || isInputBad) {
    return "Score is invalid";
  } else if (tooManyDigitsAfterDecimal(scoreNumber)) {
    return "Only 2 digits are allowed after decimal";
  } else if (scoreNumber < minScore || scoreNumber > maxScore) {
    return `Score must be between ${minScore} to ${maxScore}`;
  }
}

// for a bad input, the value of an input field is set to '' and is indistinguishable from '' or blank input. So, we are checking validity.badInput for verification
export function getYearOfCompletionErrorMessage(inputRef, year, required) {
  const isInputBad = inputRef.current
    ? inputRef.current.validity.badInput
    : true;
  const yearNumber = Number(year);

  // if (!inputRef.current) {
  //   return "";
  // }

  // required fields can't be blank
  //else
  if (yearNumber === 0) {
    if (required) {
      return "Year of Completion can't be blank";
    } else {
      return "";
    }
  }

  // remaining validations
  else if (isNaN(yearNumber) || isInputBad || !Number.isInteger(yearNumber)) {
    return "Year of Completion is invalid";
  } else if (
    yearNumber < minYearOfCompletion ||
    yearNumber > maxYearOfCompletion
  ) {
    return `Year of Completion must be between ${minYearOfCompletion} to ${maxYearOfCompletion}`;
  } else {
    return "";
  }
}

export function getCTCErrorMessage(inputRef, ctc) {
  if (!inputRef.current) {
    return "";
  }

  var isInputBad = inputRef.current.validity.badInput;
  const scoreNumber = Number(ctc);

  // remaining validations
  if (isNaN(scoreNumber) || isInputBad) {
    return "CTC is invalid";
  } else if (tooManyDigitsAfterDecimal(scoreNumber)) {
    return "Only 2 digits are allowed after decimal";
  } else if (scoreNumber < minCTC || scoreNumber > maxScore) {
    return `CTC must be ${minScore} or more`;
  }
}
